import { r as registerInstance, h, a as Host } from './index-BntG-4Sv.js';
import { T as TooltipUtil } from './tooltip-util-HoM2klPV.js';
import './onto-tooltip-placement-D_J2-9CG.js';

const ontoTooltipCss = ":host{display:block}.tooltip-box{width:max-content;font-size:0.875rem;font-weight:400;line-height:1.5;max-width:var(--gw-tooltip-max-width);pointer-events:auto;opacity:1 !important;color:var(--gw-tooltip-color) !important;background-color:var(--gw-tooltip-background) !important;border-radius:var(--gw-tooltip-border-radius) !important}.tooltip-box .tooltip-arrow{width:10px;height:10px}.tooltip-box .tooltip-arrow::before{content:\"\";position:absolute;width:10px;height:10px;background-color:var(--gw-tooltip-background);transform:rotate(45deg)}.tooltip-box .tooltip-arrow[data-placement=top]::before{border-bottom-color:transparent;border-right-color:transparent}.tooltip-box .tooltip-arrow[data-placement=bottom]::before{border-top-color:transparent;border-left-color:transparent}.tooltip-box .tooltip-arrow[data-placement=right]::before{border-bottom-color:transparent;border-left-color:transparent}.tooltip-box .tooltip-arrow[data-placement=left]::before{border-top-color:transparent;border-right-color:transparent}.tooltip-box .tooltip-content{overflow-wrap:anywhere;position:relative;z-index:1;padding:var(--gw-tooltip-padding)}";

const OntoTooltip = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        /**
         * Clears tooltips associated with elements that have been removed from the DOM.
         *
         * @param records - An array of MutationRecord objects, each representing a change to the DOM tree.
         *                  The function iterates over these records to find and clear tooltips for removed nodes.
         */
        this.clearTooltipsForRemovedElements = (records) => {
            for (const record of records) {
                for (const node of Array.from(record.removedNodes)) {
                    if (this.removeTooltipFromNode(node)) {
                        // stop iterating if a tooltip is found and destroyed,
                        // since we can't have more than one tooltip
                        return;
                    }
                }
            }
        };
        /**
         * Recursively removes tooltips from a given node and its children.
         *
         * This function checks if the given node is an HTMLElement and has an associated tooltip.
         * If a tooltip is found, it is destroyed. If not, the function then recursively checks all child nodes.
         *
         * @param node - The DOM node to check for tooltips.
         * @returns A boolean indicating whether a tooltip was found and destroyed (true) or not (false).
         */
        this.removeTooltipFromNode = (node) => {
            if (node instanceof HTMLElement) {
                const tooltipInstance = TooltipUtil.getTooltipInstance(node);
                if (tooltipInstance) {
                    TooltipUtil.destroyTooltip(node);
                    return true;
                }
                node.childNodes.forEach(this.removeTooltipFromNode);
            }
            return false;
        };
    }
    /**
     * Checks if the target of the 'mouseover' event has tooltip configuration.
     * If it does, creates a tooltip instance and displays it.
     *
     * @param event - The 'mouseover' event triggered on a document element.
     */
    onMouseover(event) {
        const target = TooltipUtil.getTooltipTarget(event.target);
        if (target) {
            this.handleTooltipTargetShow(target);
        }
        const tooltip = TooltipUtil.getTooltip(event.target);
        if (tooltip) {
            this.handleTooltipShow(tooltip);
        }
    }
    handleTooltipShow(tooltip) {
        const tooltipTarget = tooltip.tooltipTarget;
        if (!tooltipTarget) {
            return;
        }
        this.handleTooltipTargetShow(tooltipTarget);
    }
    handleTooltipTargetShow(tooltipTarget) {
        const tooltipInstance = TooltipUtil.getOrCreateTooltipInstance(tooltipTarget);
        tooltipInstance.show();
    }
    /**
     * Destroys the tooltip if it is present.
     *
     * @param event - The 'mouseout' event triggered on a document element.
     */
    onMouseout(event) {
        const target = TooltipUtil.getTooltipTarget(event.target);
        if (target) {
            this.handleTooltipTargetHide(target, event.relatedTarget);
        }
        const tooltip = TooltipUtil.getTooltip(event.target);
        if (tooltip) {
            this.handleTooltipHide(tooltip, event.relatedTarget);
        }
    }
    handleTooltipHide(tooltip, relatedTarget) {
        const tooltipTarget = tooltip.tooltipTarget;
        if (!tooltipTarget) {
            return;
        }
        if (!relatedTarget || !tooltip.contains(relatedTarget)) {
            this.handleTooltipTargetHide(tooltipTarget, relatedTarget);
        }
    }
    handleTooltipTargetHide(tooltipTarget, relatedTarget) {
        // Ensure the mouse is completely outside the target and its children
        if (!relatedTarget || !(tooltipTarget === null || tooltipTarget === void 0 ? void 0 : tooltipTarget.contains(relatedTarget))) {
            const tooltipInstance = TooltipUtil.getTooltipInstance(tooltipTarget);
            if (tooltipInstance) {
                TooltipUtil.destroyTooltip(tooltipTarget);
            }
        }
    }
    connectedCallback() {
        this.handleRemovedNodes();
    }
    disconnectedCallback() {
        var _a;
        (_a = this.observer) === null || _a === void 0 ? void 0 : _a.disconnect();
    }
    render() {
        return h(Host, { key: '6ccc31b44f2c2f04492f26d313d012b66052445a' });
    }
    /**
     * Initializes a MutationObserver to monitor the document body for removed nodes.
     * When nodes are removed, it triggers the clearing of tooltips associated with those nodes.
     */
    handleRemovedNodes() {
        this.observer = new MutationObserver(this.clearTooltipsForRemovedElements);
        this.observer.observe(document.body, { childList: true, subtree: true });
    }
};
OntoTooltip.style = ontoTooltipCss;

export { OntoTooltip as onto_tooltip };
